import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Music, Share2, Copy, Check, Bell, AlertTriangle } from "lucide-react";
import { getReleaseDetail, getPresaveByRelease, createPresave } from "@/lib/afmApi";
import { useAuth } from "@/context/AuthContext";
import { fmtUSD, fmtNum, fmtDate } from "@/lib/format";
import { Loading, ErrorState, SectionTitle } from "@/components/common";
import { StatusBadge, RELEASE_STATUS } from "@/components/StatusBadge";
import { SimuladoBadge } from "@/components/SimuladoBadge";
import { ReleaseAI } from "@/components/ReleaseAI";
import { CreativeCanvas } from "@/components/CreativeCanvas";
import TakedownRequest from "@/components/TakedownRequest";
import ArtistDistribution from "@/components/ArtistDistribution";

export default function ReleaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rel, setRel] = useState(null);
  const [status, setStatus] = useState("loading");
  const [presave, setPresave] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setStatus("loading");
    getReleaseDetail(id)
      .then((d) => { setRel(d); setStatus("ok"); })
      .catch(() => setStatus("error"));
    getPresaveByRelease(id).then((d) => setPresave(d?.id ? d : d?.presave?.id ? d.presave : null)).catch(() => setPresave(null));
  };

  useEffect(load, [id]);

  const presaveUrl = presave ? `${window.location.origin}/presave/${presave.id}` : "";

  const makePresave = async () => {
    setCreating(true);
    try {
      const d = await createPresave(user.id, id);
      const p = d?.id ? d : d?.presave;
      if (!p?.id) throw new Error("sin id");
      setPresave(p);
      toast.success("Pre-save creado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el pre-save");
    } finally { setCreating(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(presaveUrl);
    setCopied(true);
    toast.success("Enlace copiado");
    setTimeout(() => setCopied(false), 1800);
  };

  if (status === "loading") return <Loading />;
  if (status === "error") return <ErrorState onRetry={load} />;

  const roy = rel.royalties || {};

  return (
    <div className="space-y-8" data-testid="release-detail-page">
      <button onClick={() => navigate("/catalog")} data-testid="back-to-catalog" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-black">
        <ArrowLeft className="h-4 w-4" /> Volver al catálogo
      </button>

      {rel.status === "needs_correction" && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="correction-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <h3 className="font-serif text-lg font-bold text-amber-900">Este lanzamiento necesita correcciones</h3>
              {(rel.admin_note || rel.note || rel.correction_note) ? (
                <p className="mt-1 text-sm text-amber-800" data-testid="correction-note">{rel.admin_note || rel.note || rel.correction_note}</p>
              ) : (
                <p className="mt-1 text-sm text-amber-800">Nuestro equipo revisó tu entrega y requiere ajustes antes de distribuir. Revisa la portada (3000×3000, sin texto promocional) y los metadatos, y vuelve a enviar.</p>
              )}
              <button onClick={() => navigate("/distribute")} data-testid="fix-resubmit-button" className="mt-3 rounded-lg bg-amber-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-amber-800">Corregir y reenviar</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="flex h-40 w-40 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
          {rel.cover_url ? <img src={rel.cover_url} alt={rel.title} className="h-full w-full object-cover" /> : <Music className="h-12 w-12 text-slate-300" />}
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge map={RELEASE_STATUS} status={rel.status} fallbackLabel={rel.status_label} />
            <SimuladoBadge liveMode={rel.live_mode} />
          </div>
          <h1 className="mt-2 font-serif text-4xl font-extrabold tracking-tight text-black">{rel.title}</h1>
          <div className="mt-2 text-sm text-slate-500">{rel.label} · {fmtDate(rel.release_date)}</div>
          <div className="mt-4 flex flex-wrap gap-6 text-sm">
            <div><span className="text-xs uppercase tracking-wider text-slate-400">ISRC</span><div className="font-mono font-semibold text-black">{rel.isrc || "—"}</div></div>
            <div><span className="text-xs uppercase tracking-wider text-slate-400">UPC</span><div className="font-mono font-semibold text-black">{rel.upc || "—"}</div></div>
          </div>
        </div>
      </div>

      {/* Pre-save */}
      <ArtistDistribution releaseId={id} />

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="presave-section">
        <SectionTitle><span className="flex items-center gap-2"><Share2 className="h-5 w-5" /> Pre-save</span></SectionTitle>
        {!presave ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="max-w-md text-sm text-slate-500">Crea una página pública de pre-save para que tus fans guarden el lanzamiento antes de salir.</p>
            <button data-testid="create-presave-button" onClick={makePresave} disabled={creating} className="rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              {creating ? "Creando…" : "Crear pre-save"}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"><Bell className="h-3.5 w-3.5" /> {fmtNum(presave.count || 0)} pre-saves</span>
              <a href={`/presave/${presave.id}`} target="_blank" rel="noopener noreferrer" data-testid="open-presave-landing" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-black"><ExternalLink className="h-3.5 w-3.5" /> Ver landing</a>
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={presaveUrl} data-testid="presave-url" className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 font-mono text-xs text-slate-700 outline-none" />
              <button onClick={copyLink} data-testid="copy-presave-link" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:border-black">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pitching IA + Smart Link */}
      <ReleaseAI releaseId={id} />

      {/* Creative Canvas */}
      <CreativeCanvas releaseId={id} />

      {/* Royalty totals */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Streams", fmtNum(roy.streams)], ["Bruto", fmtUSD(roy.gross_usd)], ["Artista (70%)", fmtUSD(roy.artist_usd)], ["Plataforma (30%)", fmtUSD(roy.platform_usd)]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{l}</div>
            <div className="mt-2 font-mono text-xl font-bold tabular-nums text-black">{v}</div>
          </div>
        ))}
      </div>

      {/* Stores links */}
      <div>
        <SectionTitle>Disponible en tiendas</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(rel.stores || []).map((s) => (
            <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`store-link-${s.key}`} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 transition-all hover:border-black">
              <span className="font-medium text-slate-800">{s.name}</span>
              <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-black" />
            </a>
          ))}
        </div>
      </div>

      {/* By store table */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="release-by-store">
        <div className="border-b border-slate-200 px-5 py-4"><SectionTitle>Regalías por plataforma</SectionTitle></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Plataforma</th>
                <th className="px-5 py-3 text-right font-semibold">Streams</th>
                <th className="px-5 py-3 text-right font-semibold">Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {(roy.by_store || []).map((r) => (
                <tr key={r.key} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-800">{r.name}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-600">{fmtNum(r.streams)}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold tabular-nums text-black">{fmtUSD(r.usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="release-manage">
        <h3 className="mb-1 font-serif text-lg font-bold text-black">Gestionar lanzamiento</h3>
        <p className="mb-3 text-sm text-slate-500">¿Subiste algo por error o necesitas bajarlo? Pide el retiro y nuestro equipo lo revisa.</p>
        <TakedownRequest releaseId={id} releaseStatus={rel.status} />
      </div>
    </div>
  );
}
